import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

const BEST_TRANSLATED = t`Best`;
const AVERAGE_TRANSLATED = t`Average`;
const WORST_TRANSLATED = t`Worst`;
const AVAILABILITY_TRANSLATED = t`Availability`;
const PERCENT_UNIT = '%';
const AVAILABILITY_PRECISION = 2;
const AVAILABILITY__METRIC = 'availPercent';
const CALLER_METRIC_NAME = 'LqmosRx';
const CALLEE_METRIC_NAME = 'LqmosTx';

const webMinMax = (function buildWeb() {
    const metric = 'avgResponse';
    const unit = 's';
    const precision = 3;
    return [
        {
            label: i18n._(BEST_TRANSLATED),
            metric: `min${metric}`,
            postfix: unit,
            metricFn: (m) => (m / 1000).toFixed(precision),
        },
        {
            label: i18n._(AVERAGE_TRANSLATED),
            metric: `${metric}`,
            postfix: unit,
            metricFn: (m) => (m / 1000).toFixed(precision),
        },
        {
            label: i18n._(WORST_TRANSLATED),
            metric: `max${metric}`,
            postfix: unit,
            metricFn: (m) => (m / 1000).toFixed(precision),
        },
        {
            label: i18n._(AVAILABILITY_TRANSLATED),
            metric: AVAILABILITY__METRIC,
            postfix: PERCENT_UNIT,
            metricFn: (m) => m.toFixed(AVAILABILITY_PRECISION),
        },
    ];
})();

const voipMinMax = (function buildVoIP() {
    const precision = 2;
    return [
        {
            label: i18n._(t`Caller Best`),
            metric: `max${CALLER_METRIC_NAME}`,
            postfix: '',
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(AVERAGE_TRANSLATED),
            metric: `avg${CALLER_METRIC_NAME}`,
            postfix: '',
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(WORST_TRANSLATED),
            metric: `min${CALLER_METRIC_NAME}`,
            postfix: '',
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(t`Callee Best`),
            metric: `max${CALLEE_METRIC_NAME}`,
            postfix: '',
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(AVERAGE_TRANSLATED),
            metric: `avg${CALLEE_METRIC_NAME}`,
            postfix: '',
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(WORST_TRANSLATED),
            metric: `min${CALLEE_METRIC_NAME}`,
            postfix: '',
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(AVAILABILITY_TRANSLATED),
            metric: AVAILABILITY__METRIC,
            postfix: PERCENT_UNIT,
            metricFn: (m) => m.toFixed(AVAILABILITY_PRECISION),
        },
    ];
})();

function getMetricData(hasAvail, metricName, unit, precision, inverted) {
    const avail = hasAvail
        ? [
              {
                  label: i18n._(AVAILABILITY_TRANSLATED),
                  metric: AVAILABILITY__METRIC,
                  postfix: PERCENT_UNIT,
                  metricFn: (m) => m.toFixed(AVAILABILITY_PRECISION),
              },
          ]
        : [];

    return [
        {
            label: i18n._(BEST_TRANSLATED),
            metric: `${inverted ? 'max' : 'min'}${metricName}`,
            postfix: unit,
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(AVERAGE_TRANSLATED),
            metric: `avg${metricName}`,
            postfix: unit,
            metricFn: (m) => m.toFixed(precision),
        },
        {
            label: i18n._(WORST_TRANSLATED),
            metric: `${inverted ? 'min' : 'max'}${metricName}`,
            postfix: unit,
            metricFn: (m) => m.toFixed(precision),
        },
        ...avail,
    ];
}

function getUnit(mainMetric) {
    return mainMetric.units || '';
}

function getFixed(mainMetric) {
    return mainMetric.multiplier || 2;
}

function isVoIPParty(agent, test, party) {
    const partyIdArray = _.get(test, `testConfig.${party}.ids`, []);
    if (partyIdArray.length < 1) {
        return false;
    }

    // eslint-disable-next-line no-underscore-dangle
    return !_.isEmpty(_.find(partyIdArray, { id: agent._id }));
}

function handleSingleVoIPMetric(tableData, test) {
    const callerParty = 'caller';
    const metricPrecision = 2;
    const metricUnits = '';
    const agent = tableData.agent || tableData;

    if (isVoIPParty(agent, test, callerParty)) {
        return getMetricData(CALLER_METRIC_NAME, metricUnits, metricPrecision);
    }

    return getMetricData(CALLEE_METRIC_NAME, metricUnits, metricPrecision);
}

const isInverted = (mainMetric, thresholds) => {
    if (!thresholds) return false;
    const metricId = mainMetric.id;
    const matching = thresholds.filter((threshold) => threshold.metricIds?.includes(metricId));
    return matching.length ? matching[0].inverted : false;
};

export function getMinMaxMeanDef(tableData, test, isSingleVoIPMetric = false) {
    const { testType } = test;
    if (!tableData || !testType) {
        return [];
    }

    if (testType.name === 'Web') {
        return webMinMax;
    }

    if (testType.group === 'VoIP') {
        if (isSingleVoIPMetric) {
            return handleSingleVoIPMetric(tableData, test);
        }

        return voipMinMax;
    }

    const hasAvail = tableData[AVAILABILITY__METRIC] !== undefined;

    const { metrics, thresholds } = testType;

    const mainMetrics = metrics.filter((item) => item.main === true || item.isMain === true);

    const mainMetric = mainMetrics.length !== 0 ? mainMetrics[0] : undefined;

    if (!mainMetric) return [];
    const inverted = isInverted(mainMetric, thresholds);
    return getMetricData(hasAvail, mainMetric.name, getUnit(mainMetric), getFixed(mainMetric), inverted);
}
